import { Component, Input, OnInit } from '@angular/core';
import { VmeCommonModule } from '../../../../../vme-common.module';
import { VmeDirectivesModule } from '../../../../../vme-directives.module';
import { VmePipesModule } from '../../../../../vme-pipes.module';
import { VMEComponentsModule } from '../../../../../vme-components.module';
import { BaseRecommendationSegmentWithParts } from '../../../../../interfaces/route-recommendations/base-recommendation-segment';
import { IRouteRecommendationItinerary } from '../../../../../interfaces/route-recommendations/route-recommendation-itinerary';
import { RouteItineraryTravelPosition } from '../../../../../enums/algo-travel-position.enum';
import { CommonModule, DatePipe } from '@angular/common';
import { RouteRecommendationTypeEnum } from '../../../../../enums/route-recommendation-type.enum';

const homeStartTypes = [
  RouteRecommendationTypeEnum.HomeToHome,
  RouteRecommendationTypeEnum.HomeToOtherAddress,
];

@Component({
  selector: 'route-itinerary-start-end-detail',
  standalone: true,
  imports: [VmeCommonModule, VmeDirectivesModule, VmePipesModule, CommonModule],
  templateUrl: './route-itinerary-start-end-detail.component.html',
  styleUrls: [
    './route-itinerary-start-end-detail.component.scss',
    '../../common/scss/route-itinerary-common.scss',
  ],
})
export class RouteItineraryStartEndDetailComponent {
  @Input() segment: BaseRecommendationSegmentWithParts;
  @Input() itinerary: IRouteRecommendationItinerary;
  @Input() travelPosition: RouteItineraryTravelPosition;

  constructor(private datePipe: DatePipe) {}

  public get routeType(): RouteRecommendationTypeEnum {
    return this.itinerary.routeRecommendationType;
  }

  public get firstPart() {
    if (!this.segment.segmentParts || this.segment.segmentParts.length === 0) {
      return null;
    }
    return this.segment.segmentParts[0];
  }

  public get lastPart() {
    if (!this.segment.segmentParts || this.segment.segmentParts.length === 0) {
      return null;
    }
    return this.segment.segmentParts[this.segment.segmentParts.length - 1];
  }

  public get locationImage(): string {
    let relevantPart = null;

    if (this.travelPosition === RouteItineraryTravelPosition.Start) {
      relevantPart = this.firstPart;
    } else if (this.travelPosition === RouteItineraryTravelPosition.End) {
      relevantPart = this.lastPart;
    }

    if (
      relevantPart &&
      this.routeType == RouteRecommendationTypeEnum.NearestTrainStationToNearestTrainStation
    ) {
      const transportTypeLower = relevantPart.transportType?.toLowerCase();
      if (['underground', 'overground'].includes(transportTypeLower)) {
        return '/assets/images/svg/london-underground.svg';
      }

      if (transportTypeLower == 'train') {
        return '/assets/images/svg/national-rail.svg';
      }
    }

    return '';
  }

  public get locationIcon(): string {
    if (homeStartTypes.includes(this.routeType)) {
      return 'fa fa-home';
    }

    return '';
  }

  public get locationName(): string {
    if (homeStartTypes.includes(this.routeType)) {
      return `${
        this.travelPosition == RouteItineraryTravelPosition.Start ? 'Leave' : 'Arrive'
      } Home`;
    }

    if (this.travelPosition == RouteItineraryTravelPosition.Start) {
      if (this.firstPart && this.firstPart.departureLocationName) {
        return `Depart from ${this.firstPart.departureLocationName}`;
      }
    } else if (this.travelPosition == RouteItineraryTravelPosition.End) {
      if (this.lastPart && this.lastPart.arrivalLocationName) {
        return `Arrive at ${this.lastPart.arrivalLocationName}`;
      }
    }

    return '';
  }

  public get supplierPostcode(): string {
    if (homeStartTypes.includes(this.routeType)) {
      return `(${this.itinerary.supplierDetail.postcode})`;
    }

    return '';
  }

  getTimeString(): string {
    let startIsoString: string | null = null;
    let endIsoString: string | null = null;

    switch (this.travelPosition) {
      case RouteItineraryTravelPosition.Start:
        startIsoString = this.itinerary.leaveHomeEarliest;
        endIsoString = this.itinerary.leaveHomeLatest;
        break;
      case RouteItineraryTravelPosition.End:
        startIsoString = this.itinerary.arriveHomeEarliest;
        endIsoString = this.itinerary.arriveHomeLatest;
        break;
      default:
        return '';
    }

    if (!startIsoString || !endIsoString) {
      return '';
    }

    const formattedStart = this.datePipe.transform(startIsoString, 'HH:mm');
    const formattedEnd = this.datePipe.transform(endIsoString, 'HH:mm');

    return `${formattedStart} - ${formattedEnd}`;
  }
}
